import React, { useState } from "react";
import BackButton from "../../components/BackButton";
import { usePageView } from "../../utils/track";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BarChart3, Crosshair, Landmark, MapPin, Medal, Star, Target, Trophy, User } from "lucide-react";
import { Breadcrumb } from "../../layouts/PublicLayout";
import { Badge, EmptyState, InfoRow, LineChart, Loading, SectionCard, Tabs } from "../../components/ui";
import { useApi } from "../../api/client";
import { PrintButton, PrintHeader } from "../../components/PrintReport";
import { usePortalLink, useIsAdminArea } from "../../utils/portalLink";
import { rowClick } from "../../utils/rowClick";
import { RecentResult, Shooter, ShooterStats } from "../../data/types";
import { formatScore, formatScoreCentres, formatAverage, formatPercent } from "../../utils/score";

type ShooterDetailData = Shooter & { recentResults?: RecentResult[] };

interface MatchResultRow {
  matchId: string;
  matchName: string;
  eventId: string | null;
  eventName: string;
  competitionId: string;
  competitionName: string;
  date: string;
  distance: string;
  rifleClass: string;
  grade: string;
  score: number;
  possible?: number;
  xCount: number;
  centreLabel?: string;
  hits?: number;
  rank: number;
  classRank?: number;
  stringIds?: string[];
}

interface EventResultRow {
  eventId: string;
  eventName: string;
  competitionId: string;
  competitionName: string;
  date: string;
  distance: string;
  rifleClass: string;
  grade: string;
  score: number;
  possible?: number;
  xCount: number;
  centreLabel?: string;
  matchesCounted?: number;
  rank: number;
  classRank?: number;
}

interface CompetitionSummary {
  competitionId: string;
  competitionName: string;
  rifleClass: string;
  grade: string;
  date: string;
  events: number;
  score: number;
  possible: number;
  xCount: number;
  bestClassRank: number | null;
}

interface ShooterResults {
  matches: MatchResultRow[];
  events: EventResultRow[];
  competitions: CompetitionSummary[];
}

/*
 * Every tab on this page is backed by real data. Tabs that had no data source at
 * all — Ranking History and Equipment — were removed rather than left as dead
 * links, and Scores History was dropped because it duplicated Match Results.
 * The left sidebar and the top tab strip drive the same state, so clicking
 * either one works.
 */
const TABS = ["Overview", "Match Results", "Day Results", "Event Results", "Statistics", "Personal Bests"] as const;
type TabName = (typeof TABS)[number];


const ordinal = (n: number) => {
  const suffix = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${suffix[(v - 20) % 10] || suffix[v] || suffix[0]}`;
};

function RankChip({ rank }: { rank: number | null | undefined }) {
  if (!rank) return <span className="text-muted">—</span>;
  return (
    <span
      className={`inline-flex min-w-[38px] justify-center rounded-md px-2 py-1 text-xs font-extrabold ${
        rank === 1 ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-500"
      }`}
    >
      {ordinal(rank)}
    </span>
  );
}

function EmptyRow({ cols, what }: { cols: number; what: string }) {
  return (
    <tr>
      <td colSpan={cols} className="td py-10 text-center text-muted">
        No {what} recorded for this competitor yet.
      </td>
    </tr>
  );
}

export default function ShooterDetail() {
  usePageView("shooter");
  const { sid } = useParams();
  const link = usePortalLink();
  const isAdmin = useIsAdminArea();
  const navigate = useNavigate();
  const { data: s, loading } = useApi<ShooterDetailData | null>(sid ? `/shooters/${sid}` : null, null);
  const { data: distStats } = useApi<ShooterStats>(sid ? `/shooters/${sid}/stats` : null, {
    byDistance: [],
    bySeason: [],
    byClass: [],
    trend: [],
    personalBest: null,
  });
  const { data: results } = useApi<ShooterResults>(sid ? `/shooters/${sid}/results` : null, {
    matches: [],
    events: [],
    competitions: [],
  });
  const [tab, setTab] = useState<TabName>("Overview");

  if (loading) return <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-5"><div className="card"><Loading /></div></div>;
  if (!s) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-5">
        <div className="card">
          <EmptyState
            title="Shooter not found"
            message="Competitors are created when a shoot workbook is imported."
            action={
              <Link to={link("/rankings")} className="text-[13px] font-bold text-primary hover:underline">
                Back to rankings
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const recentResults = s.recentResults ?? [];
  // Every match this competitor has shot, summarised — points taken over points
  // available, and their best single card on the same scale.
  const career = distStats.career ?? { matches: 0, avg: 0, best: 0, xCount: 0, percent: null, bestPercent: null };
  const byDistance = distStats.byDistance;
  const bySeason = distStats.bySeason ?? [];
  const byClass = distStats.byClass ?? [];
  const form = distStats.trend ?? [];

  /*
   * Form over time, oldest first so the chart reads left to right — and plotted
   * as a percentage of each match's possible.
   *
   * Plotted on raw score it was unreadable and worse than unreadable: a 15-shot
   * match towered over a 10-shot one shot better, and the line fell off a cliff
   * every time the programme changed. A competitor reading it would conclude
   * they had collapsed when they had simply entered a shorter match.
   *
   * A match with no possible on record cannot be placed on that scale, so it is
   * left out rather than plotted at zero.
   */
  const percentOf = (r: MatchResultRow) => (r.possible ? (r.score / r.possible) * 100 : null);
  const trend = [...results.matches]
    .reverse()
    .map((r) => ({ label: r.distance || r.matchName.slice(0, 12), value: percentOf(r) }))
    .filter((p): p is { label: string; value: number } => p.value !== null);

  // "Best" is the highest proportion of the possible, not the biggest number:
  // 60 out of 60 beats 74 out of 90, and on raw score it did not.
  const bestMatch = results.matches.reduce<MatchResultRow | null>(
    (a, r) => (!a || (percentOf(r) ?? -1) > (percentOf(a) ?? -1) ? r : a),
    null
  );
  const bestCentres = results.matches.reduce<MatchResultRow | null>((a, r) => (!a || r.xCount > a.xCount ? r : a), null);
  const bestEvent = results.events.reduce<EventResultRow | null>((a, r) => (!a || r.score > a.score ? r : a), null);
  const bests = [
    bestMatch && { label: "Highest Match Score", date: `${bestMatch.matchName} · ${bestMatch.date}`, value: formatScoreCentres(bestMatch.score, bestMatch.xCount, bestMatch.centreLabel) },
    bestCentres && {
      label: `Highest ${bestCentres.centreLabel ?? "V"} Count`,
      date: `${bestCentres.matchName} · ${bestCentres.date}`,
      value: String(bestCentres.xCount),
    },
    bestEvent && { label: "Best Aggregate", date: `${bestEvent.eventName} · ${bestEvent.date}`, value: formatScoreCentres(bestEvent.score, bestEvent.xCount, bestEvent.centreLabel) },
    ...byDistance.map((d) => ({
      label: `Best at ${d.distance}`,
      date: `${d.events} match${d.events === 1 ? "" : "es"}`,
      value: formatScore(d.best),
    })),
  ].filter(Boolean) as { label: string; date: string; value: string }[];

  const wins = results.matches.filter((r) => (r.classRank ?? r.rank) === 1).length;
  const highlights = [
    results.matches.length ? `${results.matches.length} scored ${results.matches.length === 1 ? "match" : "matches"} on record` : null,
    wins ? `Won ${wins} of ${results.matches.length} matches in class` : null,
    byDistance.length ? `Strongest at ${byDistance.reduce((a, d) => ((d.percent ?? 0) > (a.percent ?? 0) ? d : a)).distance}` : null,
    s.classes?.length ? `Entered in ${s.classes.join(", ")}` : null,
  ].filter(Boolean) as string[];

  const stats = [
    { icon: Trophy, value: s.competitions, label: "Competitions", sub: "Participated" },
    { icon: Crosshair, value: s.matches, label: "Matches", sub: "Scored" },
    { icon: Medal, value: s.podiums, label: "Podium Finishes", sub: "Top 3 in class" },
    /*
     * Percentages, not totals. Matches run out of 50, 75, 100 and 600 depending
     * on the programme and the class, so an "average score" of 55.33 says which
     * matches this competitor entered rather than how well they shot them.
     */
    { icon: BarChart3, value: formatPercent(career.percent), label: "Average", sub: "All seasons" },
    { icon: Star, value: formatPercent(career.bestPercent), label: "Personal Best", sub: "All seasons" },
  ];


  /* ------------------------------ Tab sections ------------------------------ */

  /*
   * Season on season — the question a shooter actually asks of their own record.
   * The change column is the point: an average in isolation says nothing, an
   * average against last year's says whether the work is paying off.
   */
  const seasonSection = (
    <SectionCard title="Season by Season" bodyClassName="p-0">
      {bySeason.length ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px]">
            <thead>
              <tr className="border-b border-line bg-slate-50">
                <th className="th">Season</th>
                <th className="th text-right">Events</th>
                <th className="th text-right">Matches</th>
                <th className="th text-right" title="Every point taken over every point available this season">Average</th>
                <th className="th text-right" title="Their own best single match, as a percentage of its possible">Personal Best</th>
                <th className="th text-right">Centres</th>
                <th className="th text-right" title="Percentage points against last season">Change</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {bySeason.map((s) => (
                <tr key={s.year}>
                  <td className="td font-bold">{s.year}</td>
                  <td className="td text-right">{s.competitions}</td>
                  <td className="td text-right">{s.matches}</td>
                  <td className="td text-right font-bold">{formatPercent(s.percent)}</td>
                  <td className="td text-right">{formatPercent(s.bestPercent)}</td>
                  <td className="td text-right">{s.xCount}</td>
                  <td className="td text-right">
                    {s.changePercent == null ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <span
                        className={`font-bold ${
                          s.changePercent > 0 ? "text-success" : s.changePercent < 0 ? "text-danger" : "text-muted"
                        }`}
                      >
                        {s.changePercent > 0 ? "+" : ""}
                        {s.changePercent.toFixed(1)} pts
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="py-6 text-center text-[13px] text-muted">No season history yet.</p>
      )}
    </SectionCard>
  );

  /*
   * Form: every match in the order it was shot, as a percentage of the possible
   * so a 600-yard TR match and a 700-yard F-Open one can share an axis at all.
   * Drawn as plain bars rather than a chart library — it is one series, and a
   * dependency for one series is a dependency to keep working forever.
   */
  const formSection = (
    <SectionCard title={`Form — last ${form.length} ${form.length === 1 ? "match" : "matches"}`} bodyClassName="p-4">
      {form.length ? (
        <>
          <div className="flex h-32 items-end gap-[3px]">
            {form.map((t, i) => {
              const pct = t.percent ?? 0;
              // 80% of the possible is a poor day, 100% a perfect one, so the
              // bar is scaled across that band rather than from zero — from zero
              // every bar looks the same height.
              const h = Math.max(4, Math.min(100, ((pct - 80) / 20) * 100));
              return (
                <div
                  key={`${t.date}-${i}`}
                  title={`${t.name} · ${t.competition} · ${formatScore(t.score)}${t.possible ? ` of ${t.possible}` : ""}${
                    t.percent != null ? ` · ${t.percent}%` : ""
                  } · ${t.date}`}
                  className="flex-1 rounded-t-sm bg-primary/70 transition-colors hover:bg-primary"
                  style={{ height: `${h}%` }}
                />
              );
            })}
          </div>
          <div className="mt-2 flex justify-between text-[12px] text-muted">
            <span>{form[0]?.date}</span>
            <span>Hover a bar for the match. Scaled 80–100% of the possible.</span>
            <span>{form[form.length - 1]?.date}</span>
          </div>
        </>
      ) : (
        <p className="py-6 text-center text-[13px] text-muted">No form history yet.</p>
      )}
    </SectionCard>
  );

  /* Where a competitor is strongest — the classes are not comparable to each
     other, so each is summarised on its own terms. */
  const classSection = (
    <SectionCard title="By Rifle Class" bodyClassName="p-0">
      {byClass.length ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px]">
            <thead>
              <tr className="border-b border-line bg-slate-50">
                <th className="th">Class</th>
                <th className="th text-right">Matches</th>
                <th className="th text-right">Average</th>
                <th className="th text-right">Personal Best</th>
                <th className="th text-right">Centres</th>
                <th className="th text-right">Of possible</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {byClass.map((c) => (
                <tr key={c.rifleClass}>
                  <td className="td font-bold">{c.rifleClass}</td>
                  <td className="td text-right">{c.matches}</td>
                  <td className="td text-right font-bold">{formatAverage(c.avg)}</td>
                  <td className="td text-right">{formatScore(c.best)}</td>
                  <td className="td text-right">{c.xCount}</td>
                  <td className="td text-right text-muted">{c.percent != null ? `${c.percent}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="py-6 text-center text-[13px] text-muted">No class statistics yet.</p>
      )}
    </SectionCard>
  );

  const distanceSection = (
    <SectionCard title="Performance by Distance" bodyClassName="p-4">
      {byDistance.length ? (
        <div className="space-y-4">
          {byDistance.map((d) => (
            <div key={d.distance} className="grid items-center gap-3 sm:grid-cols-[130px_1fr_210px]">
              <div className="text-sm font-bold text-ink">{d.distance}</div>
              <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary/60 to-primary transition-all"
                  style={{ width: `${Math.max(0, Math.min(100, d.percent ?? 0))}%` }}
                />
              </div>
              <div className="text-right text-[13px] text-muted">
                <b className="text-ink">{formatPercent(d.percent)}</b> · personal best {formatPercent(d.bestPercent)} ·{" "}
                {d.events} {d.events === 1 ? "match" : "matches"}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="py-6 text-center text-[13px] text-muted">No range statistics yet.</p>
      )}
    </SectionCard>
  );

  const trendSection = trend.length > 1 && (
    <SectionCard
      className="mt-6"
      title={`Form — percentage of possible (${trend.length} ${trend.length === 1 ? "match" : "matches"})`}
      bodyClassName="grid gap-6 p-4 lg:grid-cols-[1fr_280px]"
    >
      <div>
        <LineChart data={trend} height={230} formatValue={(v) => `${v.toFixed(1)}%`} />
        <div className="mt-2 flex items-center gap-2 text-[13px] text-muted">
          <span className="inline-block h-0.5 w-6 rounded bg-primary" /> Percentage of possible
        </div>
      </div>
      <div className="rounded-xl bg-primary-soft/70 p-4">
        <h4 className="text-sm font-bold text-ink">Performance Highlights</h4>
        <ul className="mt-3 space-y-3">
          {highlights.map((h, i) => (
            <li key={i} className="flex gap-2.5 text-[13px] leading-snug text-slate-600">
              <Star size={14} className="mt-0.5 shrink-0 text-primary" />
              {h}
            </li>
          ))}
        </ul>
      </div>
    </SectionCard>
  );

  const matchResultsTable = (
    <SectionCard
      title={`Match Results (${results.matches.length})`}
      action={
        isAdmin ? (
          <Link to={`/admin/shot-logs?sid=${s.sid}`} className="text-sm font-semibold text-primary hover:underline">
            Full shot log →
          </Link>
        ) : undefined
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-line bg-slate-50">
              <th className="th">Date</th>
              <th className="th">Event</th>
              <th className="th">Match</th>
              <th className="th">Distance</th>
              <th className="th">Class</th>
              <th className="th text-right">Score</th>
              <th className="th text-right">Centres</th>
              <th className="th text-center">Class Rank</th>
              <th className="th text-center">Target</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {!results.matches.length && <EmptyRow cols={9} what="match results" />}
            {results.matches.map((r) => (
              <tr
                key={`${r.matchId}-${r.rifleClass}`}
                onClick={() => r.eventId && navigate(link(`/competitions/${r.competitionId}/events/${r.eventId}/matches/${r.matchId}`))}
                className="cursor-pointer transition-colors hover:bg-slate-50"
              >
                <td className="td whitespace-nowrap text-muted">{r.date}</td>
                <td className="td">
                  <Link to={link(`/competitions/${r.competitionId}`)} onClick={(e) => e.stopPropagation()} className="hover:text-primary">
                    {r.competitionName}
                  </Link>
                </td>
                <td className="td font-semibold">
                  {r.eventId ? (
                    <Link
                      to={link(`/competitions/${r.competitionId}/events/${r.eventId}/matches/${r.matchId}`)}
                      className="hover:text-primary"
                    >
                      {r.matchName}
                    </Link>
                  ) : (
                    r.matchName
                  )}
                </td>
                <td className="td text-muted">{r.distance}</td>
                <td className="td">
                  {r.rifleClass} <span className="text-muted">{r.grade}</span>
                </td>
                <td className="td text-right font-bold">
                  {formatScore(r.score)}
                  {r.possible ? <span className="ml-1 text-xs font-semibold text-muted">/ {r.possible}</span> : null}
                </td>
                <td className="td text-right">
                  {r.xCount} <span className="text-xs text-muted">{r.centreLabel ?? "V"}</span>
                </td>
                <td className="td text-center"><RankChip rank={r.classRank ?? r.rank} /></td>
                <td className="td text-center">
                  <span className="inline-flex justify-center gap-1">
                    {(r.stringIds ?? []).map((id) => (
                      <Link
                        key={id}
                        to={link(`/strings/${id}`)}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-line text-primary transition-colors hover:bg-primary-soft"
                        title="View bullet impacts"
                      >
                        <Target size={13} />
                      </Link>
                    ))}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );

  const eventResultsTable = (
    <SectionCard title={`Day Results — each day's aggregate (${results.events.length})`}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px]">
          <thead>
            <tr className="border-b border-line bg-slate-50">
              <th className="th">Date</th>
              <th className="th">Event</th>
              <th className="th">Day</th>
              <th className="th">Class</th>
              <th className="th text-center">Ranges</th>
              <th className="th text-right">Aggregate</th>
              <th className="th text-right">Centres</th>
              <th className="th text-center">Class Rank</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {!results.events.length && <EmptyRow cols={8} what="event aggregates" />}
            {results.events.map((r) => (
              <tr
                key={`${r.eventId}-${r.rifleClass}`}
                onClick={() => navigate(link(`/competitions/${r.competitionId}/events/${r.eventId}`))}
                className="cursor-pointer transition-colors hover:bg-slate-50"
              >
                <td className="td whitespace-nowrap text-muted">{r.date}</td>
                <td className="td">
                  <Link to={link(`/competitions/${r.competitionId}`)} onClick={(e) => e.stopPropagation()} className="hover:text-primary">
                    {r.competitionName}
                  </Link>
                </td>
                <td className="td font-semibold">
                  <Link to={link(`/competitions/${r.competitionId}/events/${r.eventId}`)} className="hover:text-primary">
                    {r.eventName}
                  </Link>
                </td>
                <td className="td">
                  {r.rifleClass} <span className="text-muted">{r.grade}</span>
                </td>
                <td className="td text-center">{r.matchesCounted ?? "—"}</td>
                <td className="td text-right font-bold">
                  {formatScore(r.score)}
                  {r.possible ? <span className="ml-1 text-xs font-semibold text-muted">/ {r.possible}</span> : null}
                </td>
                <td className="td text-right">
                  {r.xCount} <span className="text-xs text-muted">{r.centreLabel ?? "V"}</span>
                </td>
                <td className="td text-center"><RankChip rank={r.classRank ?? r.rank} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );

  const competitionResultsTable = (
    <SectionCard title={`Event Results (${results.competitions.length})`}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="border-b border-line bg-slate-50">
              <th className="th">Date</th>
              <th className="th">Event</th>
              <th className="th">Class</th>
              <th className="th text-center">Days</th>
              <th className="th text-right">Total Score</th>
              <th className="th text-right">Centres</th>
              <th className="th text-center">Best Class Rank</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {!results.competitions.length && <EmptyRow cols={7} what="competition results" />}
            {results.competitions.map((r) => (
              <tr
                key={`${r.competitionId}-${r.rifleClass}`}
                onClick={() => navigate(link(`/competitions/${r.competitionId}`))}
                className="cursor-pointer transition-colors hover:bg-slate-50"
              >
                <td className="td whitespace-nowrap text-muted">{r.date}</td>
                <td className="td font-semibold">
                  <Link to={link(`/competitions/${r.competitionId}`)} onClick={(e) => e.stopPropagation()} className="hover:text-primary">
                    {r.competitionName}
                  </Link>
                </td>
                <td className="td">
                  {r.rifleClass} <span className="text-muted">{r.grade}</span>
                </td>
                <td className="td text-center">{r.events}</td>
                <td className="td text-right font-bold">
                  {formatScore(r.score)}
                  {r.possible ? <span className="ml-1 text-xs font-semibold text-muted">/ {r.possible}</span> : null}
                </td>
                <td className="td text-right">{r.xCount}</td>
                <td className="td text-center"><RankChip rank={r.bestClassRank} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );

  const personalBests = (
    <SectionCard title="Personal Bests" bodyClassName="px-5 py-2">
      {bests.length ? (
        bests.map((b) => (
          <div key={b.label} className="flex items-center justify-between border-b border-line py-3.5 last:border-0">
            <div>
              <div className="text-sm font-bold text-ink">{b.label}</div>
              {b.date && <div className="text-xs text-muted">{b.date}</div>}
            </div>
            <div className="text-xl font-extrabold text-ink">{b.value}</div>
          </div>
        ))
      ) : (
        <p className="py-8 text-center text-[13px] text-muted">No results yet, so there are no personal bests to show.</p>
      )}
    </SectionCard>
  );

  const overview = (
    <>
      {/* Stat tiles */}
      <div className="card grid grid-cols-2 divide-x divide-line sm:grid-cols-3 lg:grid-cols-5">
        {stats.map((x) => (
          <div key={x.label} className="px-5 py-5">
            <x.icon size={22} className="text-primary" />
            <div className="mt-2.5 text-[26px] font-extrabold leading-none text-ink">{x.value}</div>
            <div className="mt-1.5 text-[13px] font-semibold text-ink">{x.label}</div>
            <div className="mt-0.5 text-xs text-muted">{x.sub}</div>
          </div>
        ))}
      </div>

      <SectionCard
        className="mt-6"
        title="Recent Results"
        action={
          <button onClick={() => setTab("Match Results")} className="text-sm font-semibold text-primary hover:underline">
            View All Results
          </button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead>
              <tr className="border-b border-line bg-slate-50">
                <th className="th">Date</th>
                <th className="th">Event</th>
                <th className="th">Event / Match</th>
                <th className="th">Class</th>
                <th className="th">Grade</th>
                <th className="th text-right">Score</th>
                <th className="th text-right">%</th>
                <th className="th text-right">Centres</th>
                <th className="th text-center">Rank</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {!recentResults.length && <EmptyRow cols={9} what="results" />}
              {recentResults.map((r, i) => (
                <tr
                  key={i}
                  /* The row opens the match it is about, where the snapshot
                     knows which one that is. */
                  onClick={
                    r.competitionId && r.eventId && r.matchId
                      ? rowClick(() =>
                          navigate(link(`/competitions/${r.competitionId}/events/${r.eventId}/matches/${r.matchId}`))
                        )
                      : undefined
                  }
                  className={`transition-colors hover:bg-slate-50 ${
                    r.competitionId && r.eventId && r.matchId ? "cursor-pointer" : ""
                  }`}
                >
                  <td className="td whitespace-nowrap text-muted">{r.date}</td>
                  <td className="td">{r.competition}</td>
                  <td className="td font-semibold">{r.eventMatch}</td>
                  <td className="td">{r.rifleClass}</td>
                  <td className="td">{r.grade}</td>
                  {/* The card as shot, then what it was worth. */}
                  <td className="td text-right font-bold">
                    {formatScore(r.score)}
                    {r.possible ? <span className="font-normal text-muted"> / {r.possible}</span> : null}
                  </td>
                  <td className="td text-right font-bold text-primary">{formatPercent(r.percent)}</td>
                  <td className="td text-right">{r.xCount}</td>
                  <td className="td text-center">
                    <span
                      className={`inline-flex min-w-[38px] justify-center rounded-md px-2 py-1 text-xs font-extrabold ${
                        r.rank === "1st" ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {r.rank}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!!recentResults.length && (
          <div className="px-5 py-4">
            <span className="text-[13px] text-muted">
              Showing the most recent {recentResults.length} of {s.matches} scored {s.matches === 1 ? "match" : "matches"}
            </span>
          </div>
        )}
      </SectionCard>

      <div className="mt-6 space-y-4">
        {seasonSection}
        {distanceSection}
      </div>
      {trendSection}
    </>
  );

  const tabContent: Record<TabName, React.ReactNode> = {
    Overview: overview,
    "Match Results": matchResultsTable,
    "Day Results": eventResultsTable,
    "Event Results": competitionResultsTable,
    Statistics: (
      <div className="space-y-4">
        {formSection}
        {seasonSection}
        {classSection}
        {distanceSection}
        {trendSection}
      </div>
    ),
    "Personal Bests": personalBests,
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-5">
      <Breadcrumb
        items={[
          { label: "Home", to: link("/") },
          { label: "Shooters", to: link("/rankings") },
          { label: `${s.name} (SID: ${s.sid})` },
        ]}
      />

      <div className="mt-3 flex flex-col gap-4 xl:flex-row">
        {/*
          There is no profile sidebar. It carried the name, SID, state, club,
          category and status — each of which the heading beside it or the
          summary below already gave — and a second copy of the tab strip. One
          owner per fact: the heading identifies the competitor, the tabs
          navigate, the summary holds what the heading has no room for.
        */}

        {/* Main */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-2xl font-extrabold tracking-tight text-ink">{s.name}</h1>
                {/* A competitor's record is something people file and email. */}
                <PrintButton label="Print record" />
              </div>
              <PrintHeader
                title={s.name}
                subtitle="Competitor Record"
                meta={["SID " + s.sid, s.club, s.classes?.length ? s.classes.join(" / ") : s.rifleClass]}
              />
              <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted">
                <span className="flex items-center gap-1.5"><User size={15} /> SID: {s.sid}</span>
                {s.club && <span className="flex items-center gap-1.5"><Landmark size={15} /> {s.club}</span>}
                <span className="flex items-center gap-1.5"><MapPin size={15} className="text-accent" /> {s.state}, {s.country}</span>
                <span className="flex items-center gap-1.5">
                  <Crosshair size={15} /> {s.classes?.length ? s.classes.join(" / ") : s.rifleClass}
                </span>
              </div>
            </div>
            <BackButton to={link("/rankings")} label="Back to Shooters" />
          </div>

          <Tabs className="mt-5" tabs={[...TABS]} active={tab} onChange={(t) => setTab(t as TabName)} />

          <div className="mt-4">{tabContent[tab]}</div>
        </div>
      </div>

      {/*
        Summary and Personal Bests sit BELOW the results, not in a third column —
        the tables above get the full width, and these read as reference cards.
      */}
      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        {/*
          Registration detail only. Name, SID, state, country, club, category
          and class are all in the heading at the top of the page; this card
          used to print every one of them a second time.
        */}
        <SectionCard title="Registration" bodyClassName="grid grid-cols-1 gap-x-10 px-5 py-3 sm:grid-cols-2">
          <div>
            <InfoRow label="Category" value={s.category} />
            <InfoRow
              label="Grade"
              value={
                s.gradeByClass && Object.keys(s.gradeByClass).length
                  ? Object.entries(s.gradeByClass)
                      .map(([cls, g]) => `${cls} ${g}`)
                      .join(" · ")
                  : s.grade
              }
            />
            {!!s.squads?.length && <InfoRow label="Squad" value={s.squads.join(", ")} />}
            {!!s.roles?.length && <InfoRow label="Official Role" value={s.roles.join(", ")} />}
            {/* Handedness was never in the entry list and could not be edited. */}
            <InfoRow label="Status" value={<Badge tone="active">{s.status}</Badge>} />
            {s.memberSince && <InfoRow label="Member Since" value={s.memberSince} />}
          </div>
        </SectionCard>

        {/* Personal Bests has a tab of its own; it does not also live here. */}
      </div>
    </div>
  );
}
